import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, CalendarDays, Users, UserPlus, Receipt,
  PoundSterling, TrendingUp, Settings, LogOut, ChevronDown,
  ChevronRight, X, Zap, Briefcase, Clock, MapPin, Bell,
  CreditCard, FileText, BarChart2, Layers, Link2, Sparkles, RefreshCw, Globe,
  GraduationCap, MessageCircle, Mail, Copy, Check, Palmtree, Truck, Info, ClipboardList
} from 'lucide-react';
import * as api from '../services/api';
import { APP_VERSION } from '../version';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Menu configuration with role permissions and tooltips
const menuGroups = [
  {
    id: 'main',
    items: [
      { id: 'overview', label: 'Dashboard', Icon: LayoutDashboard, tooltip: 'Your daily overview & stats', roles: ['Owner', 'Administrator', 'Creator', 'Reporting', 'Planner', 'Worker'] },
      { id: 'day-pilot', label: 'Day Pilot', Icon: Zap, tooltip: "Today's jobs with map & navigation", roles: ['Owner', 'Administrator', 'Creator', 'Job Editor', 'Planner', 'Worker'] },
      { id: 'work-planner', label: 'Work Planner', Icon: CalendarDays, tooltip: 'View & plan jobs by date', roles: ['Owner', 'Administrator', 'Creator', 'Planner', 'Worker'] },
      { id: 'job-list', label: 'Jobs', Icon: Briefcase, tooltip: 'All jobs list & search', roles: ['Owner', 'Administrator', 'Creator', 'Job Editor', 'Planner'] },
      { id: 'empire', label: 'Empire', Icon: Globe, tooltip: 'Map of all your customers', isDynamic: true, roles: ['Owner', 'Administrator', 'Creator', 'Reporting', 'Planner'] },
      { id: 'owing', label: 'Customers Owing', Icon: PoundSterling, tooltip: 'Chase outstanding payments', roles: ['Owner', 'Administrator'] },
      { id: 'learning', label: 'Tutorials', Icon: GraduationCap, tooltip: 'Video tutorials & guides', roles: ['Owner', 'Administrator', 'Creator', 'Reporting', 'Planner', 'Worker'] },
      { id: 'help-assistant', label: 'AI Staff', Icon: MessageCircle, tooltip: 'AI departments: Help, IT Support, Accounts', roles: ['Owner', 'Administrator', 'Creator', 'Reporting', 'Planner', 'Worker', 'Job Editor', 'Marketing', 'Canvasser'] },
    ]
  },
  {
    id: 'clients',
    label: 'Customers',
    roles: ['Owner', 'Administrator', 'Creator', 'Marketing', 'Canvasser'],
    items: [
      { id: 'customers', label: 'Customers', Icon: Users, tooltip: 'Manage your customer database', roles: ['Owner', 'Administrator', 'Creator', 'Marketing'] },
      { id: 'leads', label: 'Leads', Icon: Sparkles, tooltip: 'New enquiries to follow up', badge: true, roles: ['Owner', 'Administrator', 'Creator', 'Marketing', 'Canvasser'] },
      { id: 'canvassing', label: 'Canvassing', Icon: ClipboardList, tooltip: 'Import canvasser sheets as customers & jobs', roles: ['Owner', 'Canvasser'] },
    ]
  },
  {
    id: 'work',
    label: 'Work',
    roles: ['Owner', 'Administrator', 'Creator', 'Job Editor', 'Planner', 'Worker'],
    items: [
      { id: 'rounds', label: 'Rounds', Icon: MapPin, tooltip: 'Group customers into routes', roles: ['Owner', 'Administrator', 'Creator', 'Planner'] },
      { id: 'quotes', label: 'Quotes', Icon: FileText, tooltip: 'Create & send quotes', roles: ['Owner', 'Administrator', 'Creator'] },
    ]
  },
  {
    id: 'money',
    label: 'Finances',
    roles: ['Owner', 'Administrator', 'Reporting'],
    items: [
      { id: 'invoices', label: 'Invoices', Icon: Receipt, tooltip: 'Create & manage invoices', roles: ['Owner', 'Administrator'] },
      { id: 'payments', label: 'Payments', Icon: CreditCard, tooltip: 'Track money received', roles: ['Owner', 'Administrator'] },
      { id: 'expenses', label: 'Expenses', Icon: TrendingUp, tooltip: 'Log business costs', roles: ['Owner', 'Administrator'] },
    ]
  },
  {
    id: 'business',
    label: 'Business',
    roles: ['Owner', 'Administrator', 'Reporting'],
    items: [
      { id: 'reporting', label: 'Reports', Icon: BarChart2, tooltip: 'Business insights & analytics', roles: ['Owner', 'Administrator', 'Reporting'] },
      { id: 'time-completed', label: 'Time Completed', Icon: Clock, tooltip: 'Worker arrival & job times', roles: ['Owner', 'Administrator'] },
      { id: 'waitlist', label: 'Waitlist', Icon: ClipboardList, tooltip: 'StreakFree waitlist signups', ownerGate: true, roles: ['Owner'] },
    ]
  },
  {
    id: 'setup',
    label: 'Setup',
    roles: ['Owner', 'Administrator'],
    items: [
      { id: 'worker-locations', label: 'Worker Locations', Icon: Truck, tooltip: 'Live GPS tracking of workers', roles: ['Owner', 'Administrator'] },
      { id: 'staff-leave', label: 'Staff Leave', Icon: Palmtree, tooltip: 'Manage worker holidays & time off', roles: ['Owner', 'Administrator'] },
      { id: 'users', label: 'Users & Teams', Icon: Users, tooltip: 'Manage staff & permissions', roles: ['Owner', 'Administrator'] },
      { id: 'services', label: 'Services', Icon: Layers, tooltip: 'Your service types & prices', roles: ['Owner', 'Administrator'] },
      { id: 'sync-centre', label: 'Sync Centre', Icon: RefreshCw, tooltip: 'Import & export data', roles: ['Owner', 'Administrator'] },
      { id: 'connected-apps', label: 'Integrations', Icon: Link2, tooltip: 'Connect Stripe, GoCardless etc', roles: ['Owner', 'Administrator'] },
      { id: 'settings', label: 'Settings', Icon: Settings, tooltip: 'Company details & preferences', roles: ['Owner', 'Administrator'] },
    ]
  }
];

const Sidebar = ({ isOpen, onClose, currentPage, setCurrentPage, user, onLogout }) => {
  // Get user role - default to Owner for main account
  const userRole = user?.role || 'Owner';
  // Start with all groups collapsed except main, business, setup - this ensures key sections are visible
  const [expandedGroups, setExpandedGroups] = useState(['main', 'business', 'setup']);
  const [stats, setStats] = useState({ jobs_today: 0, earnings_this_week: 0 });
  const [companyLogo, setCompanyLogo] = useState('');
  const [emailAddress, setEmailAddress] = useState(null);
  const [waitlistAllowed, setWaitlistAllowed] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Fetch live stats and user settings for sidebar
  useEffect(() => {
    const loadStats = async () => {
      try {
        const token = api.getToken();
        if (!token) return;
        
        // Fetch dashboard stats
        const res = await fetch(`${API_URL}/api/dashboard/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setStats(data);
        }
        
        // Fetch user settings for logo
        const settingsRes = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (settingsRes.ok) {
          const settings = await settingsRes.json();
          setCompanyLogo(settings.company_logo || '');
        }
        
        // Fetch email forwarding address
        const emailRes = await fetch(`${API_URL}/api/settings/email-token`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (emailRes.ok) {
          const emailData = await emailRes.json();
          setEmailAddress(emailData.email_address);
        }

        // Check waitlist access (owner-only tab)
        const wlRes = await fetch(`${API_URL}/api/waitlist/access`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (wlRes.ok) {
          const wlData = await wlRes.json();
          setWaitlistAllowed(!!wlData.allowed);
        }
      } catch (err) {
        console.error('Error loading sidebar stats:', err);
      }
    };
    loadStats();
    const interval = setInterval(loadStats, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const handleItemClick = (itemId) => {
    setCurrentPage(itemId);
    onClose();
  };

  const toggleGroup = (groupId) => {
    console.log('Toggle group clicked:', groupId);
    setExpandedGroups(prev => {
      const newState = prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId];
      console.log('New expanded groups:', newState);
      return newState;
    });
  };

  // Get user initials
  const getInitials = (name) => {
    if (!name) return 'PP';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const userInitials = getInitials(user?.name);
  const userName = user?.name || 'User';
  const companyName = user?.company_name || 'StreakFree';
  const roleLabel = userRole === 'Worker' ? 'WORKER'
    : (userRole === 'Owner' || userRole === 'Administrator') ? 'ADMIN'
    : userRole.toUpperCase();

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:relative inset-y-0 left-0 z-50
          w-64 bg-white border-r border-zinc-200
          transform transition-transform duration-200
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col h-full max-h-screen overflow-hidden
        `}
        style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Logo & Brand */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-100">
          <div className="flex items-center gap-3">
            {companyLogo ? (
              <div className="w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center bg-white border border-zinc-200">
                <img src={companyLogo} alt="Logo" className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shadow-sm" style={{ background: 'linear-gradient(135deg, var(--theme-accent), var(--theme-button-hover))' }}>
                <span className="text-white font-bold text-sm">{getInitials(companyName)}</span>
              </div>
            )}
            <div>
              <h1 className="font-bold text-zinc-900 text-sm leading-none">{companyName}</h1>
              <p className="text-[10px] text-zinc-400 mt-0.5">Window Cleaning CRM</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 hover:bg-zinc-100 rounded-lg lg:hidden"
          >
            <X size={18} className="text-zinc-400" />
          </button>
        </div>

        {/* User Card */}
        <div className="p-3">
          <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-zinc-50 to-zinc-100/50 rounded-xl">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-sm" style={{ backgroundColor: 'var(--theme-accent)' }}>
              {userInitials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-zinc-900 text-sm truncate">{userName}</p>
              <p className="text-xs text-zinc-500 truncate">{companyName}</p>
            </div>
            <button className="p-1.5 hover:bg-white rounded-lg transition-colors">
              <Bell size={16} className="text-zinc-400" />
            </button>
          </div>
        </div>

        {/* Navigation - Scrollable area */}
        <nav className="flex-1 overflow-y-auto overscroll-contain px-3 pb-3 min-h-0">
          {menuGroups.map((group) => {
            // Filter items based on user role
            const visibleItems = group.items.filter(item => 
              (!item.roles || item.roles.includes(userRole)) &&
              (!item.ownerGate || waitlistAllowed)
            );
            
            // Skip entire group if no visible items or group is role-restricted
            if (visibleItems.length === 0) return null;
            if (group.roles && !group.roles.includes(userRole)) return null;
            
            const isExpanded = expandedGroups.includes(group.id);
            const hasLabel = !!group.label;
            
            return (
              <div key={group.id} className="mb-1">
                {hasLabel && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleGroup(group.id);
                    }}
                    className="w-full flex items-center justify-between px-3 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                    data-testid={`toggle-${group.id}`}
                    type="button"
                  >
                    <span>{group.label}</span>
                    <ChevronDown 
                      size={16} 
                      className={`transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`} 
                    />
                  </button>
                )}
                
                {(isExpanded || !hasLabel) && (
                  <div className={hasLabel ? 'space-y-0.5' : 'space-y-0.5 mb-2'}>
                    {visibleItems.map((item) => {
                      const Icon = item.Icon;
                      const isActive = currentPage === item.id;
                      
                      // Special label for Empire - show company name
                      const displayLabel = item.id === 'empire' 
                        ? `${companyName} Empire`
                        : item.label;
                      
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleItemClick(item.id)}
                          data-testid={`nav-${item.id}`}
                          title={item.tooltip}
                          className={`
                            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                            transition-all duration-150 group relative
                            ${isActive 
                              ? 'text-white' 
                              : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900'
                            }
                          `}
                          style={isActive ? { backgroundColor: 'var(--theme-accent)' } : {}}
                        >
                          <div className="relative">
                            <Icon 
                              size={18} 
                              strokeWidth={isActive ? 2 : 1.5}
                              className={isActive ? 'text-white' : 'text-zinc-400 group-hover:text-zinc-600'}
                            />
                            {item.badge && (
                              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--theme-accent)' }} />
                            )}
                          </div>
                          <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'} truncate`}>
                            {displayLabel}
                          </span>
                          {isActive && (
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full" style={{ backgroundColor: 'var(--theme-primary)' }} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        {/* Footer — scrolls with the menu (not sticky) */}
        <div className="p-3 mt-2 border-t border-zinc-100 bg-white">
          {/* App logo above the forward email address */}
          <div className="flex justify-center mb-3">
            <img
              src={companyLogo || '/icons/icon-180.png'}
              alt="Logo"
              data-testid="sidebar-footer-logo"
              className="h-12 w-12 rounded-xl object-contain shadow-sm"
            />
          </div>
          {/* Email Forwarding Address - HIDE FOR WORKERS */}
          {emailAddress && userRole !== 'Worker' && (
            <div className="mb-3 p-2 bg-blue-50 rounded-lg">
              <p className="text-[10px] text-blue-600 font-medium mb-1 flex items-center gap-1">
                <Mail size={10} />
                Your Forward Email Address:
              </p>
              <div className="flex items-center gap-1">
                <p className="text-[11px] text-blue-700 font-mono truncate flex-1" title={emailAddress}>
                  {emailAddress}
                </p>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(emailAddress);
                    setEmailCopied(true);
                    setTimeout(() => setEmailCopied(false), 2000);
                  }}
                  className="p-1 hover:bg-blue-100 rounded transition-colors"
                  title="Copy email address"
                >
                  {emailCopied ? (
                    <Check size={12} className="text-emerald-600" />
                  ) : (
                    <Copy size={12} className="text-blue-600" />
                  )}
                </button>
              </div>
            </div>
          )}
          
          {/* Quick Stats - LIVE DATA - HIDE PRICE FOR WORKERS */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button 
              onClick={() => handleItemClick('work-planner')}
              className="bg-zinc-50 hover:bg-zinc-100 rounded-lg p-2 text-center transition-colors"
            >
              <p className="text-lg font-bold text-zinc-900">{stats.jobs_today || 0}</p>
              <p className="text-[10px] text-zinc-500">Jobs Today</p>
            </button>
            {userRole !== 'Worker' && (
              <button 
                onClick={() => handleItemClick('work-planner')}
                className="rounded-lg p-2 text-center transition-colors"
                style={{ backgroundColor: 'color-mix(in srgb, var(--theme-accent) 15%, white)' }}
              >
                <p className="text-lg font-bold" style={{ color: 'var(--theme-accent)' }}>
                  £{(stats.week_total || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
                <p className="text-[10px] text-zinc-500">This Week</p>
              </button>
            )}
          </div>

          {/* Logout */}
          {onLogout && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onLogout();
              }}
              data-testid="logout-btn"
              type="button"
              className="w-full flex items-center justify-center gap-2 py-4 text-zinc-500 hover:text-red-600 hover:bg-red-50 active:bg-red-100 rounded-lg transition-colors text-sm font-medium cursor-pointer touch-manipulation"
            >
              <LogOut size={18} />
              Sign Out
            </button>
          )}

          {/* Version / connection / role — Squeegee-style footer */}
          <div className="mt-1 pt-2 text-center space-y-1" data-testid="sidebar-app-info">
            <p className="text-[11px] font-semibold text-zinc-500 flex items-center justify-center gap-1" data-testid="sidebar-version">
              <Info size={11} className="text-zinc-400" />
              v{APP_VERSION}
            </p>
            <p className="text-[11px] font-bold text-zinc-700 flex items-center justify-center gap-1.5 tracking-wide" data-testid="sidebar-connection-status">
              <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-red-500'}`} />
              {isOnline ? 'CONNECTED' : 'OFFLINE'}
            </p>
            <p className="text-[10px] font-semibold text-zinc-400 tracking-widest" data-testid="sidebar-user-role">
              {roleLabel}
            </p>
          </div>
        </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
